import { StriimTypography, StriimButton, StriimFeedbackBlock } from "@striim/striim-ui";
import { Box, SvgIcon } from "@mui/material";
import classes from "./test-connection.styles";
import Error from "../../generic/icon/wizard-icon/errorFilled.svg";
import Success from "../../generic/icon/wizard-icon/successFilled.svg";
import MissingDriver from "./missing-driver";
import utils from "core/utils";

import WithTooltip from "../tooltip/tooltip";
import { TEST_PRIMARY_CONNECTION } from "../fd-form-builder/action-button";
import ProgressIndicator from "../../modules/apps/pages/manage-striim/components/progress-indicator";
import { PlugConnected, Retry } from "../../../src/generic/icon/customIcons";
import { styles } from "../../modules/flow-designer/vts/selectTables.styles";

interface TestConnectionMsgBoxProps {
    loading: boolean;
    testConnection: () => void;
    connectionSuccess?: boolean;
    errorMsg?: string;
    unavailableDriverDBType?: string;
    lastValidationDate: string;
    testCxnBtnName: string;
    handler: string;
    isModified: boolean;
}

const TestConnectionMsgBox = ({
    loading,
    testConnection,
    connectionSuccess,
    errorMsg,
    unavailableDriverDBType,
    lastValidationDate,
    testCxnBtnName,
    handler,
    isModified
}: TestConnectionMsgBoxProps) => {
    const getValidateButton = (retest: boolean) => {
        const adapterName = handler ? utils.getName(handler) : "";
        let btnTooltip = null;

        if (adapterName === "OJet") {
            btnTooltip = "Testing Capture Connection";
            if (testCxnBtnName === TEST_PRIMARY_CONNECTION) {
                btnTooltip = "Testing Source Database Connection";
            }
        }

        return WithTooltip(
            <StriimButton
                disabled={loading}
                onClick={testConnection}
                sx={[classes.testConnectionBtn, styles.footerButton]}
                variant="text"
                startIcon={<SvgIcon component={Retry} sx={styles.retryIcon} />}
            >
                {retest ? "Retest" : "Retry"}
            </StriimButton>,
            btnTooltip
        );
    };

    return !connectionSuccess && !errorMsg && !unavailableDriverDBType ? (
        loading ? (
            <StriimFeedbackBlock
                content={
                    loading ? (
                        <Box width="100%">
                            <StriimTypography sx={classes.header} variant="body4">
                                Testing connection
                            </StriimTypography>
                            <Box mt={1}>
                                <ProgressIndicator variant="indeterminate" percentage={100} />
                            </Box>
                        </Box>
                    ) : null
                }
                variant="info"
                data-testid="test-connection-init"
            />
        ) : (
            <StriimButton
                disabled={loading}
                onClick={testConnection}
                sx={classes.testConnectionBtn}
                variant="secondary"
                data-testid="test-connection-button"
            >
                Test Connection
            </StriimButton>
        )
    ) : unavailableDriverDBType ? (
        <StriimFeedbackBlock
            content={
                <StriimTypography sx={classes.header} variant="body4">
                    <MissingDriver databaseType={unavailableDriverDBType} />
                </StriimTypography>
            }
            variant="critical"
            data-testid="test-connection-no-driver"
            footer={getValidateButton(false)}
        />
    ) : (connectionSuccess || errorMsg) && isModified ? (
        <StriimFeedbackBlock
            content={
                <StriimTypography sx={classes.header} variant="body4" data-testid="test-connection-changes">
                    Connection details changed.
                    <br />
                    Validate the connection again.
                </StriimTypography>
            }
            variant="warning"
            data-testid="test-connection-changes"
            footer={
                <StriimButton
                    onClick={() => {
                        testConnection();
                    }}
                    sx={[classes.testConnectionBtn, styles.footerButton]}
                    variant="text"
                    startIcon={<SvgIcon component={PlugConnected} sx={styles.retryIcon} />}
                    data-testid="test-connection-button"
                >
                    Test connection
                </StriimButton>
            }
            customStyles={styles.customBlock}
        />
    ) : connectionSuccess ? (
        <StriimFeedbackBlock
            header={lastValidationDate}
            content={
                <StriimTypography sx={classes.header} variant="body4" data-testid="test-connection-success">
                    Connection tested successfully
                </StriimTypography>
            }
            variant="success"
            data-testid="test-connection-success"
            footer={getValidateButton(true)}
            customStyles={classes.feedbackBlockHeader}
        />
    ) : errorMsg ? (
        <StriimFeedbackBlock
            header={lastValidationDate}
            content={
                <StriimTypography sx={classes.header} variant="body4" data-testid="test-connection-error">
                    {errorMsg}
                </StriimTypography>
            }
            variant="critical"
            data-testid="test-connection-error"
            footer={getValidateButton(false)}
            customStyles={styles.customBlock}
        />
    ) : null;
};

export default TestConnectionMsgBox;
